import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
} from '@mui/material'
import { NavLink, useLocation } from 'react-router-dom'
import { useAppSelector } from '@store/hooks'
import { useBetaMode } from '@hooks/useBetaMode'
import { isAdminPanelRole } from '@utils/adminPermissions'
import { isNavItemActive } from './sidebarUtils'
import { APP_SHELL_NAV_DRAWER_ID, DRAWER_WIDTH } from './layoutConstants'
import { headerSafeAreaSpacerSx } from './mainContentStyles'
import { useTemporaryAppDrawer } from './useAppShellDrawer'
import {
  primaryItems,
  insightItems,
  getVisibleAccountItems,
  utilityItems,
  adminEntryItem,
  type NavItemDef,
} from './appNavConfig'

const itemButtonSx = {
  color: 'primary.contrastText',
  textDecoration: 'none',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'primary.contrastText',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
    '& .MuiListItemText-primary': { fontWeight: 600 },
  },
  '& .MuiListItemIcon-root': { color: 'primary.contrastText', minWidth: 40 },
  '& .MuiListItemText-primary': { fontWeight: 500 },
}

const drawerPaperSx = {
  width: DRAWER_WIDTH,
  boxSizing: 'border-box',
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  borderRight: '1px solid',
  borderRightColor: 'primary.dark',
  zIndex: (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer,
  display: 'flex',
  flexDirection: 'column',
}

function SectionDivider() {
  return <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
}

type SidebarNavProps = {
  onNavClick?: () => void
}

export function SidebarNav({ onNavClick }: SidebarNavProps) {
  const location = useLocation()
  const { user } = useAppSelector((state) => state.auth)
  const { beta } = useBetaMode()

  const isAdmin = isAdminPanelRole(user?.role)
  const visibleAccountItems = getVisibleAccountItems(beta)

  const renderItem = (item: NavItemDef) => {
    const active = isNavItemActive(item.path, location.pathname)
    return (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          component={NavLink}
          to={item.path}
          selected={active}
          aria-current={active ? 'page' : undefined}
          onClick={onNavClick}
          sx={itemButtonSx}
        >
          <ListItemIcon>
            <item.Icon />
          </ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    )
  }

  return (
    <>
      <Box sx={headerSafeAreaSpacerSx} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List disablePadding aria-label="Main navigation">
          {primaryItems.map(renderItem)}

          <SectionDivider />
          {insightItems.map(renderItem)}

          <SectionDivider />
          {visibleAccountItems.map(renderItem)}

          <SectionDivider />
          {utilityItems.map(renderItem)}

          {isAdmin && (
            <>
              <SectionDivider />
              {renderItem(adminEntryItem)}
            </>
          )}
        </List>
      </Box>
    </>
  )
}

type SidebarProps = {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
  const isTemporaryDrawer = useTemporaryAppDrawer()

  const nav = <SidebarNav onNavClick={isTemporaryDrawer ? onMobileClose : undefined} />

  if (isTemporaryDrawer) {
    return (
      <Drawer
        id={APP_SHELL_NAV_DRAWER_ID}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
      >
        {nav}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': drawerPaperSx,
      }}
    >
      {nav}
    </Drawer>
  )
}

export default Sidebar
